import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

export interface Job {
  id: string;
  url: string;
  title: string;
  company: string;
  location?: string;
  salary?: string;
  description_markdown: string;
  deadline?: string;
  status: 'active' | 'expired' | 'applied' | 'rejected' | 'not-interested' | 'archived' | 'withdrawn';
  status_marked_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export const useJobs = () => {
  return useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/jobs`, { headers });
      if (!res.ok) throw new Error('Failed to fetch jobs');
      return res.json() as Promise<Job[]>;
    },
  });
};

export const useCreateJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (url: string) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/jobs`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ url }),
      });
      if (!res.ok) throw new Error('Failed to create job');
      return res.json() as Promise<Job>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

export const useDeleteJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/jobs/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error('Failed to delete job');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

export const useScrapeJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (url: string) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/jobs/scrape`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ url }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to scrape job');
      }
      return res.json() as Promise<Job>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

export const useGenerateAssessment = () => {
  return useMutation({
    mutationFn: async ({
      jobId,
      providerId,
      resumeContent,
      promptTemplateId,
    }: {
      jobId: string;
      providerId: string;
      resumeContent: string;
      promptTemplateId?: string;
    }) => {
      const res = await fetch(`${API_BASE}/jobs/${jobId}/assess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providerId,
          resumeContent,
          promptTemplateId,
        }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to generate assessment');
      }
      return res.json();
    },
  });
};

export const useRefreshJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      method = 'basic',
      providerId,
      promptTemplateId,
    }: {
      jobId: string;
      method?: 'basic' | 'llm';
      providerId?: string;
      promptTemplateId?: string;
    }) => {
      const res = await fetch(`${API_BASE}/jobs/${jobId}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method,
          providerId,
          promptTemplateId,
        }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to refresh job');
      }
      return res.json() as Promise<{ data: Job }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

export const useUpdateJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      updates,
    }: {
      jobId: string;
      updates: Partial<Job>;
    }) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/jobs/${jobId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update job');
      }
      return res.json() as Promise<Job>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};
